import React, { useState, useEffect } from 'react';
import { guardarConfiguracionEmpresa, obtenerConfiguracionEmpresa } from '../servicios/api';
import { normalizarIdentificacionFiscal, validarIdentificacionFiscal } from '../validarIdentificacionFiscal';

const ModalConfigurarFacturas = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    nifCif: '',
    fiscalAddress: '',
    phone: '',
    email: '',
    iban: '',
    invoiceFooter: '',
    visualFormat: 'clasica',
  });
  const [logoPreview, setLogoPreview] = useState(null);

  // Cargar configuración al abrir el modal
  useEffect(() => {
    if (isOpen) {
      const cargarConfiguracion = async () => {
        try {
          const response = await obtenerConfiguracionEmpresa();
          if (response.exito && response.configuracion) {
            const config = response.configuracion;
            setFormData({
              companyName: config.company_name || '',
              nifCif: config.nif_cif || '',
              fiscalAddress: config.fiscal_address || '',
              phone: config.phone || '',
              email: config.email || '',
              iban: config.iban || '',
              invoiceFooter: config.invoice_footer || '',
              visualFormat: config.visual_format || 'clasica',
            });
            if (config.logo_path) {
              let logoPath = config.logo_path.trim();
              if (logoPath.endsWith(':')) {
                logoPath = logoPath.slice(0, -1);
              }
              setLogoPreview(logoPath);
            }
          } else {
            // No hay configuración guardada o error, mantener valores por defecto
            console.log("No se encontró configuración de empresa o hubo un error.", response);
          }
        } catch (error) {
          console.error("Error al cargar la configuración de la empresa:", error);
          alert("Error al cargar la configuración de la empresa. Inténtalo de nuevo más tarde.");
        }
      };
      cargarConfiguracion();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: name === 'nifCif' ? value.toUpperCase() : value
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9); // Convert to JPEG with 90% quality
          setLogoPreview(dataUrl);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    document.getElementById('logoUpload').value = ''; // Clear file input
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.companyName || !formData.nifCif) {
      alert('Por favor, completa los campos "Nombre o Razón Social" y "NIF / CIF". Son obligatorios.');
      return;
    }
    if (!validarIdentificacionFiscal(formData.nifCif)) {
      alert('Introduce un NIF, NIE o CIF válido para la empresa.');
      return;
    }

    try {
      const dataToSave = {
        ...formData,
        nifCif: normalizarIdentificacionFiscal(formData.nifCif),
        logoPath: logoPreview, // Save Data URL directly for now
      };
      const response = await guardarConfiguracionEmpresa(dataToSave);
      if (response.exito) {
        alert(response.mensaje);
        onClose();
      } else {
        alert(`Error al guardar la configuración: ${response.mensaje}`);
      }
    } catch (error) {
      console.error("Error al guardar la configuración:", error);
      alert("Error al conectar con el servidor para guardar la configuración.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-slate-900 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-slate-700 text-slate-100">
        {/* Encabezado del Modal */}
        <div className="flex justify-between items-center p-4 border-b border-slate-700 bg-slate-800">
          <h2 className="text-lg font-bold text-white">Configuración de Datos de Empresa para Facturación</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Contenido del Formulario */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label htmlFor="companyName" className="block text-xs font-medium text-slate-400 mb-1">Nombre o Razón Social:</label>
              <input type="text" id="companyName" name="companyName" value={formData.companyName} onChange={handleChange} placeholder="Tu Empresa S.L." className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
            
            <div className="form-group">
              <label htmlFor="nifCif" className="block text-xs font-medium text-slate-400 mb-1">NIF / CIF:</label>
              <input type="text" id="nifCif" name="nifCif" value={formData.nifCif} onChange={handleChange} placeholder="B12345678" className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="fiscalAddress" className="block text-xs font-medium text-slate-400 mb-1">Dirección Fiscal:</label>
            <input type="text" id="fiscalAddress" name="fiscalAddress" value={formData.fiscalAddress} onChange={handleChange} placeholder="Calle Ejemplo 12, Granada" className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label htmlFor="phone" className="block text-xs font-medium text-slate-400 mb-1">Teléfono:</label>
              <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="958 123 456" className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
            
            <div className="form-group">
              <label htmlFor="email" className="block text-xs font-medium text-slate-400 mb-1">Email de contacto:</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="contacto@tuempresa.com" className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="iban" className="block text-xs font-medium text-slate-400 mb-1">IBAN (Cuenta bancaria para pagos):</label>
            <input type="text" id="iban" name="iban" value={formData.iban} onChange={handleChange} placeholder="ES12 3456 7890 1234 5678 9012" className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>
          
          <div className="bg-slate-800 rounded-lg p-4 my-4 text-center border border-slate-700">
            <div id="logoPreview" className={`inline-flex items-center gap-3 text-3xl font-bold text-blue-400 mb-4 ${logoPreview ? 'hidden' : ''}`}>
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl"></div>
              <span>LOGO</span>
            </div>
            {logoPreview && (
              <img src={logoPreview} className="max-w-xs max-h-48 rounded-lg shadow-md mx-auto mb-4" alt="Logo de empresa" />
            )}
            <div className="text-sm text-slate-400 leading-tight mb-4">
              Resolución recomendada: máximo 325x200px. El logo aparecerá en<br/>
              facturas, presupuestos y albaranes creados a partir de ahora
            </div>
            
            <div className="flex gap-3 justify-center flex-wrap">
              <input type="file" id="logoUpload" name="logoUpload" accept="image/png,image/jpg,image/jpeg,image/gif" className="hidden" onChange={handleLogoUpload} />
              <button type="button" className="px-4 py-2 border-2 border-blue-600 rounded-md bg-slate-900 text-blue-400 text-sm font-semibold hover:bg-blue-600 hover:text-white transition-colors flex items-center gap-2" onClick={() => document.getElementById('logoUpload').click()}>
                Subir Logo
              </button>
              {logoPreview && (
                <button type="button" className="px-4 py-2 border-2 border-red-600 rounded-md bg-slate-900 text-red-400 text-sm font-semibold hover:bg-red-600 hover:text-white transition-colors flex items-center gap-2" onClick={handleRemoveLogo}>
                  Eliminar Imagen
                </button>
              )}
            </div>
          </div>
          
          <div className="bg-slate-800 rounded-lg p-4 my-4 border border-slate-700">
            <div className="font-semibold text-blue-400 mb-2 flex items-center">
              Pie fijo para todas las facturas:
            </div>
            <textarea id="invoiceFooter" name="invoiceFooter" rows="3" value={formData.invoiceFooter} onChange={handleChange} placeholder="Inscrito en el Registro Mercantil...\nGarantía válida por 3 meses..." className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"></textarea>
          </div>
          
          <div className="mt-6">
            <div className="form-group">
              <label htmlFor="visualFormat" className="block text-xs font-medium text-slate-400 mb-1 flex items-center">
                Formato visual de factura:
              </label>
              <select id="visualFormat" name="visualFormat" value={formData.visualFormat} onChange={handleChange} className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none bg-no-repeat bg-right-center" style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`, backgroundSize: '16px', paddingRight: '40px' }}>
                <option value="clasica">Clásica</option>
                <option value="moderna">Moderna</option>
                <option value="minimalista">Minimalista</option>
                <option value="corporativa">Corporativa</option>
              </select>
            </div>
          </div>
          
          {/* Pie del Modal */}
          <div className="flex justify-end items-center p-4 border-t border-slate-700 bg-slate-900 mt-6">
            <button type="button" onClick={onClose} className="bg-slate-600 hover:bg-slate-700 text-white font-bold py-2 px-4 rounded text-sm mr-2">
              Cancelar
            </button>
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm">
              Usar configuración
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalConfigurarFacturas;